import { Component, OnDestroy, OnInit } from '@angular/core';
import { SearchReport, SearchReportStatus } from '../../../../models/search-report.model';
import { SearchReportQuery, SearchReportQueryBatch } from '../../../../models/search-report-query.model';
import { SearchReportService } from '../../../../services/search-report.service';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { UserConfigs } from '../../../../models/user-configs.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { FilterService } from '../../../../services/filter.service';
import { Filter } from '../../../../models/review/filter.model';
import { AddReportQueryComponent } from '../dialogs/add-report-query/add-report-query.component';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { combineLatest, Subscription, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { SearchReportExportRequest } from '../../../../models/search-report-export-request.model';
import { HelpService } from '../../../../services/help.service';
import { SelectFilterNewComponent } from 'src/app/components/dialogs/select-filter-new/select-filter-new.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserResource } from '../../../../models/resource/user.resource';
import { NotificationManager } from '../../../../managers/notification.manager';
import { ProcessingManager } from '../../../../managers/processing.manager';
import { SearchHitReportRequest } from '../../../../models/ui/job-request/search-hit-report-request.model';
import { BrowseManager } from '../../../../managers/browse.manager';
import { UserManager } from '../../../../managers/user.manager';
import { ExportSdk } from '../../../../sdks/export.sdk';
import { Store } from "@ngrx/store";
import { selectIsIndexingAndIsProcessing } from "@store/global/global.selectors";

@Component({
    selector: 'lex-search-reports',
    templateUrl: './search-reports.component.html',
    styleUrls: ['./search-reports.component.scss'],
    standalone: false
})
export class SearchReportsComponent implements OnInit, OnDestroy {

	selectedReport: SearchReport = null;
	editReportId: string = '';
	searchReports: SearchReport[] = [];
	userConfig: UserConfigs;
	filterString: string = '';
	filter: Filter;
	isGraphView = true;
	createdByUsers: UserResource[];
	isProcessingOrIndexing = false;
	legacySearchSyntaxWarning: string[] = [];

	get ReportStatus(): typeof SearchReportStatus { return SearchReportStatus; }

	private pollSearchReportStatusSub: Subscription;
	private subscription = new Subscription();

	constructor(
		public filterService: FilterService,
		private searchReportService: SearchReportService,
		private userConfigService: UserConfigsService,
		private dialog: MatDialog,
		private userManager: UserManager,
		private helpService: HelpService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private snackbar: MatSnackBar,
		private notificationManager: NotificationManager,
		private processingManager: ProcessingManager,
		private browseManager: BrowseManager,
		private exportSdk: ExportSdk,
		private store: Store
	) { }

	ngOnInit() {
		this.helpService.updateHelpLink('analysis-menu/search-term-report');
		this.loadReports();
		this.userConfigService.getUserConfigs().subscribe((config: UserConfigs) => { this.userConfig = config });
		this.subscription.add(this.store.select(selectIsIndexingAndIsProcessing).subscribe(status =>
			this.isProcessingOrIndexing = status.isIndexing || status.isProcessing
		));
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	loadReports() {
		const searchReportsObservable = this.searchReportService.getSearchReports();
		const createdByObservable = this.userManager.getSearchReportCreatedBy();
		this.subscription.add(
			combineLatest([searchReportsObservable, createdByObservable])
				.pipe(take(1))
				.subscribe(data => {
					// data is an array of last subscriptions (next) of each of the observables above in the same order
					this.searchReports = data[0].sort((a: SearchReport, b: SearchReport) => a.dateTimeCreated < b.dateTimeCreated ? 1 : -1);

					if (this.activatedRoute.snapshot.queryParams['SearchReportId'])
						this.setReport(this.searchReports.find(sr => sr.searchReportId == this.activatedRoute.snapshot.queryParams['SearchReportId']));
					else if (this.selectedReport)
						this.setReport(this.searchReports.find(sr => sr.searchReportId == this.selectedReport.searchReportId));

					if (this.searchReports.some(sr => sr.runStatus == SearchReportStatus.Running))
						this.setPollSearchReportStatusSub();
					else if (this.searchReports.every(sr => sr.runStatus === SearchReportStatus.Done) && this.pollSearchReportStatusSub)
						this.pollSearchReportStatusSub.unsubscribe();

					this.createdByUsers = data[1];
				}));
	}

	setReport(report: SearchReport) {
		this.clearFilter();
		if (!report) {
			this.router.navigate([], { relativeTo: this.activatedRoute });
			report = null;
			return;
		}
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: { SearchReportId: report.searchReportId }
		});
		this.selectedReport = report;
		if (report.filterId) {
			this.filterService.getFilter(this.selectedReport.filterId).subscribe((data: Filter) => {
				this.setFilter(data);
			});
		}
		this.verifyQuerySyntax();
	}

	isSelectedReport(report: SearchReport): boolean {
		if (!this.selectedReport)
			return false;

		return report.searchReportId == this.selectedReport.searchReportId;
	}

	createReport() {
		const newReport = new SearchReport();
		newReport.title = this.getNextTitle();
		newReport.caseId = this.userConfig.currentCase.caseId;
		newReport.createdById = this.userConfig.userId;
		this.searchReportService.createSearchReport(newReport).subscribe((report: SearchReport) => {
			this.selectedReport = report;
			this.loadReports();
		});
	}

	private getNextTitle(incremented: number = null): string {
		let index = incremented ?? this.searchReports.length + 1;
		const tempTitle = `Search Report v.${index}`;
		const titleIndex = this.searchReports.findIndex(r => r.title === tempTitle);
		if (titleIndex !== -1)
			return this.getNextTitle(index += 1);
		return tempTitle;
	}

	deleteReport(report: SearchReport) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Search Term Report`,
			message: `Delete ${report.title}?
          <br/> The report will be permanently deleted from the case.
          <br/> This action cannot be undone.`
		};
		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.selectedReport = null;
				this.searchReportService.deleteSearchReport(report.searchReportId).subscribe(() => { this.loadReports() });
			}
		});
	}

	updateReport(report: SearchReport) {
		this.searchReportService.updateSearchReport(report).subscribe();
		this.editReportId = '';
	}

	getTotalHitCount(queries: SearchReportQuery[]): number {
		let totalHitCount = 0;
		queries.forEach(q => totalHitCount += q.hitCount);
		return totalHitCount;
	}

	showAddQueryDialog() {
		const dialogRef = this.dialog.open(AddReportQueryComponent, {
			data: {
				existingQueryStrings: this.selectedReport.searchReportQueries.map(srq => srq.query)
			}
		});

		dialogRef.afterClosed().subscribe((queryStrings: string[]) => {
			if (queryStrings) {
				const queries: SearchReportQueryBatch = new SearchReportQueryBatch;
				queries.queries = [];
				queryStrings.forEach(q => {
					if (q == '')
						return;

					const reportQuery = new SearchReportQuery();
					reportQuery.searchReportId = this.selectedReport.searchReportId;
					reportQuery.query = q;
					queries.queries.push(reportQuery);
				});

				this.searchReportService.addSearchReportQueries(queries).subscribe(data => {
					const validQueries: SearchReportQuery[] = [];
					const failedQueries: SearchReportQuery[] = [];
					data.forEach(q => {
						if (q.valid)
							validQueries.push(q);
						else
							failedQueries.push(q);
					});
					// Using concat instead of push to force the change detect of the table.
					this.selectedReport.searchReportQueries = this.selectedReport.searchReportQueries.concat(validQueries);
					this.verifyQuerySyntax();
					if (failedQueries.length > 0) {
						const message = `Queries failed to save: ${failedQueries.map(q => q.query).join(', ')}`;
						this.snackbar.open(message, 'X', { duration: 5000 });
					}
				});
			}
		});
	}

	removeReportQuery(deletedQuery: SearchReportQuery) {
		this.selectedReport.searchReportQueries = this.selectedReport.searchReportQueries.filter(q => q.searchReportQueryId !== deletedQuery.searchReportQueryId);
		this.verifyQuerySyntax();
	}

	cloneReport(report: SearchReport) {
		if (report.searchParameter?.engineType != 'opensearch')
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to clone: Search Term Report does not match the case's engine type.`,
				type: 'error',
				ttl: 5000
			});
		else {
			const clone = new SearchReport();
			clone.title = this.getNextTitle();
			clone.caseId = report.caseId;
			clone.createdById = report.createdById;
			clone.filterId = report.filterId;
			clone.searchParameter = report.searchParameter;
			clone.searchReportQueries = report.searchReportQueries;
			this.searchReportService.createSearchReport(clone).subscribe((clonedReport) => {
				this.selectedReport = clonedReport;
				this.loadReports();
			});
		}
	}

	addSearchReortJobToQueue(report: SearchReport) {
		if (this.isProcessingOrIndexing) {
			this.notificationManager.displaySnackBarMessage({
				message: 'Unable to start processing report because items are still being processed or indexed.',
				type: 'warn',
				ttl: 10000
			});
			return;
		}
		if (report.searchParameter?.engineType !== 'opensearch') {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to run: Search Term Report does not match the case's engine type.`,
				type: 'error',
				ttl: 5000
			});
			return;
		}
		const request = new SearchHitReportRequest();
		request.stepParams = {
			searchReportId: `${report.searchReportId}`
		};
		this.processingManager.scheduleStep({ stepRequest: request }).pipe(
			switchMap(() => {
				report.runStatus = SearchReportStatus.Running;
				report.dateTimeRun = new Date().toISOString();
				return this.searchReportService.updateSearchReport(report);
			})
		).subscribe((report) => {
			if (report) this.setPollSearchReportStatusSub()
		});
	}

	selectFilter() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '800px';
		dialogConfig.data = {
			filter: this.filter ? JSON.parse(this.filter.filterJson) : null
		};

		const dialogRef = this.dialog.open(SelectFilterNewComponent, dialogConfig);

		dialogRef.afterClosed().subscribe((data) => {
			if (!data || data.isCancelled) return;

			if (data.filter) {
				this.filterService.saveFilter(data.filter).subscribe((filter: Filter) => {

					this.selectedReport.filterId = filter.savedFilterId;

					this.setFilter(filter);

					this.updateReport(this.selectedReport);
				});
			} else {
				this.selectedReport.filterId = null;
				this.filterString = '';
			}
		});
	}

	setFilter(filter: Filter) {
		this.filter = filter;
		this.filterString = filter.filterText;
	}

	getCreatedByName(createdById: string): string {
		return this.createdByUsers.find(u => u.id === createdById).userName;
	}

	verifyQuerySyntax() {
		this.legacySearchSyntaxWarning = [];
		if (this.selectedReport.runStatus !== SearchReportStatus.Created) return;

		this.selectedReport.searchReportQueries.forEach((searchReportQuery) => {
			const message = this.browseManager.verifyOpenSearchQuerySyntax(searchReportQuery.query);
			if (message !== '')
				this.legacySearchSyntaxWarning.push(`Warning: ${searchReportQuery.query} - ${message}`);
		});
	}

	clearWarning(warning: string) {
		if (this.legacySearchSyntaxWarning.length === 1)
			this.legacySearchSyntaxWarning = [];
		else
			this.legacySearchSyntaxWarning = this.legacySearchSyntaxWarning.filter((message) => { return warning !== message });
	}

	private setPollSearchReportStatusSub(): void {
		this.pollSearchReportStatusSub = this.searchReportService.getAllSearchReportStatus()
			.subscribe(data => {
				this.searchReports.forEach(sr => {
					const report = data.find(r => r.searchReportId === sr.searchReportId);
					if (report) {
						if (sr.runStatus !== report.runStatus) {
							sr.runStatus = report.runStatus;
							if (report.runStatus === SearchReportStatus.Done) // load reports if job hase finished.
								this.loadReports();
						}
					}
				});
			});
		this.subscription.add(this.pollSearchReportStatusSub);
	}

	toggleIcon(): string {
		return this.isGraphView ? 'toc' : 'bar_chart';
	}

	exportToLog() {
		const exportRequest = new SearchReportExportRequest();
		exportRequest.searchReportId = this.selectedReport.searchReportId;
		exportRequest.filterString = this.filterString;
		exportRequest.parameter = this.selectedReport.searchParameter;
		this.exportSdk.exportSearchReport(exportRequest).subscribe((url) => {
			window.open(url, '_blank');
		});
	}

	clearFilter() {
		this.filter = null;
		this.filterString = null;
	}

	public navigateToBrowse(): void {
		this.router.navigate(['/review/browse'], { queryParams: { SearchReportId: this.selectedReport.searchReportId, SearchReportQueryIds: this.selectedReport.searchReportQueries.map(q => q.searchReportQueryId) } });
	}
}
